import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { finalize, Subscription, take } from 'rxjs';
import * as uuid from 'uuid';
import { ToastService } from 'src/app/modules/services/toast/toast.service';
import { LoadingService } from '../../loading/loading.service';
import { FormatUtilsService } from 'src/app/modules/services/format-utils/format-utils.service';
import { cepValidator } from 'src/app/modules/shared/validators/cep-validator';
import { EnderecoService } from 'src/app/modules/services/endereco/endereco.service';

@Component({
  selector: 'app-modal-endereco-entidade',
  templateUrl: './modal-endereco-entidade.component.html',
  styleUrls: ['./modal-endereco-entidade.component.scss'],
})
export class ModalEnderecoEntidadeComponent {
  @Input() isEditMode: boolean = false;
  @Input() data: any = {};
  form: FormGroup;
  status: boolean = true;
  id: any = null;
  tempId: any = null;
  subscriptions: Subscription[] = [];

  // Estados
  allEstados: any[] = [];
  estadoInput: string = '';
  selectEstadoId: any = null;
  selectedEstado: any = null;
  
  // Cidade
  allCidades: any[] = [];
  cidadeInput: string = '';
  selectCidadeId: any = null;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private modalCtrl: ModalController,
    private loadingService: LoadingService,
    private enderecoService: EnderecoService,
    private formatUtilsService: FormatUtilsService
  ) {
    this.form = this.fb.group({
      rua: [null, [Validators.required]],
      numero: [null, [Validators.required]],
      complemento: [null],
      bairro: [null, [Validators.required]],
      cidade: [null, [Validators.required]],
      estado: [null, [Validators.required]],
      estadoInput: [null],
      cidadeInput: [null],
      cep: ['', [Validators.required, cepValidator()]],
    });
  }

  ngOnInit(): void {
    this.loadEstados();

    if (this.isEditMode && this.data) {
      const estado = this.allEstados.filter(
        (estado) => estado.nome === this.data.uf
      )[0];

      this.form.patchValue({
        rua: this.data.rua,
        numero: this.data.numero,
        complemento: this.data.complemento,
        bairro: this.data.bairro,
        cidade: this.data.cidade,
        estado: estado?.id,
        estadoInput: this.data.uf,
        cep: this.formatUtilsService.formatCep(this.data.cep),
      });
      this.id = this.data.id;
      this.tempId = this.data.tempId;
      this.estadoInput = this.data.uf;
      this.selectEstadoId = estado?.id;
      this.selectedEstado = estado;
    }

    this.form.get('cep')?.valueChanges.subscribe((cep: string) => {
      this.getEnderecoByCep(cep);
    });
  }

  loadEstados() {
    this.allEstados = this.enderecoService.getEstados();
  }

  loadCidades() {
    this.loadingService.showLoading();
    this.subscriptions.push(
      this.enderecoService
        .getCidades(this.selectEstadoId)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response: any) => {
            this.allCidades = response.content;
          },
          (erro: any) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  onSelectEstado(value: any) {
    this.setFormValue(value, 'estado');

    if (value) {
      const estado = this.allEstados.filter((estado) => estado.id === value)[0];
      this.selectedEstado = estado;
    }
  }

  onSelectEstadoInput(value: any) {
    this.setFormValue(value, 'estadoInput');
  }

  onSelectCidade(value: any) {
    this.setFormValue(value, 'cidade');
  }

  onSelectCidadeInput(value: any) {
    this.setFormValue(value, 'cidadeInput');
  }

  setFormValue(value: any, formInput: any) {
    if (value) {
      return this.form.patchValue({ [formInput]: value });
    }
    return this.form.patchValue({ [formInput]: null });
  }

  resetCidade() {
    this.setFormValue(null, 'cidade');
    this.setFormValue(null, 'cidadeInput');
    this.cidadeInput = '';
    this.selectCidadeId = null;
    this.allCidades = [];
  }

  close() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  onSubmit() {
    const formValues = this.form.value;
    const id = this.isEditMode ? this.data.id : '';
    const tempId = this.isEditMode ? this.tempId : uuid.v1();

    const formattedObj = {
      id: id,
      tempId: tempId,
      rua: formValues.rua,
      numero: formValues.numero,
      complemento: formValues.complemento,
      bairro: formValues.bairro,
      cidade: formValues.cidade,
      estado: formValues.estadoInput,
      cep: formValues.cep.substring(0, 9),
      ufId: this.selectedEstado.id,
      uf: this.selectedEstado.nome,
      status: this.status,
    };
    const msg = this.isEditMode
      ? 'Endereço editado com sucesso'
      : 'Endereço inserido com sucesso';

    this.toastService.showSuccess(msg);
    return this.modalCtrl.dismiss(formattedObj, 'confirm');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onCepSearch() {
    this.getEnderecoByCep(this.form.get('cep')?.value);
  }

  
  getEnderecoByCep(cep: string) {
    const cepFormatted = cep.replace('-', '');
    if (cepFormatted.length !== 8) return;

    this.loadingService.showLoading();

    this.subscriptions.push(
      this.enderecoService
        .getEnderecoByCep(cepFormatted)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response: any) => {
            const { descricao, descricaoBairro, cidade, uf } = response;
            const estado = this.allEstados.filter(
              (estado) => estado.nome === uf
            )[0];

            this.form.patchValue({
              rua: descricao,
              bairro: descricaoBairro,
              cidade: cidade.descricao,
              estado: estado?.id,
              estadoInput: estado?.nome,
            });

            this.estadoInput = estado?.nome;
            this.selectEstadoId = estado?.id;
            this.selectedEstado = estado;
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }
}
